import React, { useContext, useEffect, useState } from "react";
import { CustomRegistrationQuestion } from "../types/RegistrationQuestion";
import { addCustomRegistrationQuestionsToEvent, addCustomRegistrationQuestionToEvent, deleteCustomRegistrationQuestion, getCustomRegistrationQuestionsForEvent, updateCustomRegistrationQuestion } from "../services/customRegistrationQuestionService";
import { useToast } from "../context/ToastContext";
import { EventContext } from "../context/EventContext";
import { useNavigate } from "react-router-dom";
import ToggleSwitch from "./ToggleSwitch";
import { ROUTES } from "../constants/routes";
import { getIcon } from "../services/iconService";

export const EditCustomQuestions: React.FC = () => {
    const [customQuestions, setCustomQuestions] = useState<CustomRegistrationQuestion[]>([]);
    const [newQuestionText, setNewQuestionText] = useState("");
    const [newQuestionOptions, setNewQuestionOptions] = useState<string[]>([]);
    const [newQuestionType, setNewQuestionType] = useState<'text' | 'select'>("text");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingText, setEditingText] = useState("");
    const [editingType, setEditingType] = useState<'text' | 'select'>("text");
    const [editingRequired, setEditingRequired] = useState(false);
    const [newQuestionRequired, setNewQuestionRequired] = useState(false);
    const [editingOptions, setEditingOptions] = useState<string[]>([]);

    const navigate = useNavigate();

    const { currentEvent } = useContext(EventContext);
    const { addToast } = useToast();

    useEffect(() => {
        loadCustomQuestions();
    }, [currentEvent?.event_id]);

    const loadCustomQuestions = async () => {
        if (!currentEvent) {
            navigate(ROUTES.HOME);
            return;
        }
        const questions = await getCustomRegistrationQuestionsForEvent(currentEvent?.event_id);
        setCustomQuestions(questions);
    };

    const toggleNewQuestionRequired = () => {
        setNewQuestionRequired(!newQuestionRequired);
    }

    const handleAdd = async () => {
        if (!currentEvent) {
            addToast('No Event Selected', true);
            return;
        }
        if (customQuestions.length >= 100) {
            addToast('Maximum number of custom questions reached.', true);
            return;
        }
        if (newQuestionType.toLowerCase() == 'select') {
            const hasEmptyOption = newQuestionOptions.some(
                option => option === ''
            );
            if (hasEmptyOption) {
                addToast('Question has an empty option.', true);
                return;
            }
        }
        if (!newQuestionText.trim()) return;
        if (newQuestionType.toLowerCase() == 'select') {
            var hasOptionError = false;
            if (newQuestionOptions.length == 0) {
                addToast('Select questions must have at least one option', true);
                return;
            } else {
                newQuestionOptions.forEach(option => {
                    if (option.length == 0) {
                        addToast('Select question options must have text', true);
                        hasOptionError = true;
                        return;
                    }
                })
                if (hasOptionError) {
                    return;
                }
            }
        }
        const customQuestion: CustomRegistrationQuestion = {
            event_id: currentEvent?.event_id,
            custom_registration_question_id: '',
            custom_question_text: newQuestionText,
            is_required: newQuestionRequired,
            question_type: newQuestionType,
            options: newQuestionType.toLowerCase() == 'select' ? newQuestionOptions : []
        };

        const success = await addCustomRegistrationQuestionToEvent(currentEvent?.event_id, customQuestion);
        setNewQuestionText("");
        setNewQuestionRequired(false);
        setNewQuestionOptions(['']);
        setNewQuestionType('text');
        loadCustomQuestions();

        if (success) {
            addToast(editingText + ' added to event.');
        } else {
            addToast('Error adding question to event, please try again;')
        }
    };

    const handleUpdate = async (id: string) => {
        if (!currentEvent) {
            return;
        }
        if (editingType == 'select') {
            var hasOptionError = false;
            if (editingOptions.length == 0) {
                addToast('Select questions must have at least one option', true);
                return;
            } else {
                editingOptions.forEach(option => {
                    if (option.length == 0) {
                        addToast('Select question options must have text', true);
                        hasOptionError = true;
                        return;
                    }
                })
                if (hasOptionError) {
                    return;
                }
            }
        }
        const customQuestion: CustomRegistrationQuestion = {
            event_id: currentEvent?.event_id,
            custom_registration_question_id: id,
            custom_question_text: editingText,
            is_required: editingRequired,
            question_type: editingType,
            options: editingType == 'select' ? editingOptions : []
        };

        const success = await updateCustomRegistrationQuestion(currentEvent.event_id, customQuestion);
        setEditingId(null);
        loadCustomQuestions();
        if (success) {
            addToast(editingText + ' updated.');
        } else {
            addToast('Error updating question for event, please try again;')
        }
    };

    const handleDelete = async (id: string) => {
        if (!currentEvent) {
            return;
        }
        if (!window.confirm("Delete this custom question?")) return;
        const success = await deleteCustomRegistrationQuestion(currentEvent.event_id, id);
        loadCustomQuestions();
        if (success) {
            addToast(editingText + ' Removed from event.');
        } else {
            addToast('Error removing question from event, please try again;')
        }
    };

    return (
        <div>

            {customQuestions.map((q) => (
                <div key={q.custom_registration_question_id} style={{ marginBottom: 12, gap: '5px' }}>
                    {editingId === q.custom_registration_question_id ? (
                        <div className="card">
                            <div className='buttonsRow'>
                                <input
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    style={{ maxWidth: '270px' }}
                                />

                            </div>
                            {editingType == 'select' && <div style={{ marginLeft: '20px' }}>
                                {editingOptions.map((option, i) => (
                                    <div key={i} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                                        <input
                                            value={option}
                                            onChange={(e) => {
                                                const updatedOptions = [...editingOptions];
                                                updatedOptions[i] = e.target.value;
                                                setEditingOptions(updatedOptions);
                                            }}
                                            placeholder={`Option ${i + 1}`}
                                            style={{ maxWidth: '300px' }}
                                            min={1}
                                            max={500}
                                        />
                                        <button
                                            type="button"
                                            className="cu-icon-button"
                                            style={{ marginBottom: '10px' }}
                                            disabled={editingOptions.length <= 1}
                                            onClick={() => {
                                                const updatedOptions = editingOptions.filter((_, idx) => idx !== i);
                                                if (updatedOptions.length == 0) {
                                                    setEditingOptions([""]);
                                                } else {
                                                    setEditingOptions(updatedOptions);
                                                }

                                            }}
                                        >
                                            <img src={getIcon('delete')} alt={'delete'}></img>
                                        </button>
                                    </div>
                                ))}

                                <button
                                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0px',  backgroundColor: 'white', outline: 'none', border: 'none', boxShadow: 'none' }}
                                    type="button"
                                    disabled={editingOptions.length > 25}
                                    onClick={() => setEditingOptions([...editingOptions, ''])}>
                                    <img style={{ height: '40px', width: '45px', aspectRatio: 'fit' }} src={getIcon('add')} alt={'Add'}></img>

                                    <span style={{ color: '#333', marginRight: '12px', backgroundColor: 'none', boxShadow: 'none' }}>New Option</span>
                                </button>
                            </div>}
                            <div className="buttonsRow">
                                <div className="buttonsLeft" />
                                <div className='buttonsRight' style={{ gap: '5px', flexWrap: 'nowrap' }}>
                                    <select
                                        value={editingType}
                                        onChange={(e) => setEditingType(e.target.value as "text" | "select")}
                                        style={{ maxWidth: '100px', marginBottom: '0px' }}
                                    >
                                        <option value="text">Text</option>
                                        <option value="select">Select</option>
                                    </select>
                                    <ToggleSwitch label={editingRequired ? 'Required' : 'Optional'} checked={editingRequired} onChange={() => { setEditingRequired(!editingRequired); }} />
                                    <button onClick={() => handleUpdate(q.custom_registration_question_id)}>Save</button>
                                    <button className='cancelButton' onClick={() => setEditingId(null)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card">
                            <div className='buttonsRow'>
                                <div className='buttonsLeft' style={{ flexDirection: 'column' }}>
                                    <span><strong>{q.custom_question_text}</strong></span>
                                    {q.question_type === 'select' && q.options && q.options?.length > 0 && (
                                        <div style={{ marginLeft: '5px' }}>
                                            <div>Options:</div>
                                            <ul style={{ marginTop: '4px', marginBottom: 0 }}>
                                                {q.options.map((opt, idx) => (
                                                    <li key={idx}>{opt}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}                                </div>
                            </div>
                            <div className="buttonsRow">
                                <div className="buttonsLeft" />
                                <div className='buttonsRight' style={{ flexWrap: 'nowrap', gap: '5px' }}>
                                    <select
                                        disabled={true}
                                        value={q.question_type}
                                        style={{ maxWidth: '100px', marginBottom: '0px' }}
                                    >
                                        <option value="text">Text</option>
                                        <option value="select">Select</option>
                                    </select>
                                    <ToggleSwitch label={q.is_required ? 'Required' : 'Optional'} disabled={true} checked={q.is_required} onChange={() => { }} />
                                    <button className="cu-icon-button" onClick={() => {
                                        setEditingId(q.custom_registration_question_id);
                                        setEditingText(q.custom_question_text);
                                        setEditingRequired(q.is_required);
                                        setEditingType(q.question_type as 'text' | 'select');
                                        setEditingOptions(q.question_type == 'select' ? q.options ?? [''] : ['']);
                                    }}
                                        disabled={currentEvent?.is_locked}
                                    ><img src={getIcon('edit')} alt={'Edit'}></img></button>
                                    <button className='cu-icon-button' onClick={() => handleDelete(q.custom_registration_question_id)}
                                        disabled={currentEvent?.is_locked}>
                                        <img src={getIcon('delete')} alt={'Delete'}></img>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))
            }

            <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <input
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                        placeholder="New custom question"
                        style={{ maxWidth: '400px' }}
                        min={1}
                        max={500}
                    />
                    <select
                        value={newQuestionType}
                        onChange={(e) => setNewQuestionType(e.target.value as "text" | "select")}
                        style={{ maxWidth: '100px' }}
                    >
                        <option value="text">Text</option>
                        <option value="select">Select</option>
                    </select>
                    <ToggleSwitch label={newQuestionRequired ? 'Required' : 'Optional'} checked={newQuestionRequired} onChange={toggleNewQuestionRequired} />
                </div>
                {/* Options inputs */}
                {newQuestionType === 'select' && (
                    <div style={{ marginBottom: '10px', marginLeft: '20px' }}>
                        {newQuestionOptions.map((option, i) => (
                            <div key={i} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                                <input
                                    value={option}
                                    onChange={(e) => {
                                        const updatedOptions = [...newQuestionOptions];
                                        updatedOptions[i] = e.target.value;
                                        setNewQuestionOptions(updatedOptions);
                                    }}
                                    placeholder={`Option ${i + 1}`}
                                    style={{ maxWidth: '300px' }}
                                    min={1}
                                    max={500}
                                />
                                <button
                                    type="button"
                                    className="cu-icon-button"
                                    style={{ marginBottom: '10px' }}
                                    disabled={newQuestionOptions.length <= 1}
                                    onClick={() => {
                                        const updatedOptions = newQuestionOptions.filter((_, idx) => idx !== i);
                                        if (updatedOptions.length == 0) {
                                            setNewQuestionOptions([""]);
                                        } else {
                                            setNewQuestionOptions(updatedOptions);
                                        }

                                    }}
                                >
                                    <img src={getIcon('delete')} alt={'delete'}></img>
                                </button>
                            </div>
                        ))}

                        <button
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0px',  backgroundColor: 'white', outline: 'none', border: 'none', boxShadow: 'none' }}
                            type="button"
                            disabled={newQuestionOptions.length > 25}
                            onClick={() => setNewQuestionOptions([...newQuestionOptions, ''])}>
                            <img style={{ height: '40px', width: '45px', aspectRatio: 'fit' }} src={getIcon('add')} alt={'Add'}></img>

                            <span style={{ color: '#555' }}>New Option</span>
                        </button>
                        <br />
                    </div>
                )}
                <div className="buttonsRow">
                    <div className="buttonsLeft" />
                    <div className="buttonsRight">
                        <button
                            onClick={handleAdd}
                            disabled={newQuestionText == '' || (newQuestionType == 'select' && newQuestionOptions.length == 0) || customQuestions.length >= 100 || currentEvent?.is_locked}
                        >
                            Add Custom Question
                        </button>
                    </div>
                </div>
            </div>
        </div >
    );
};
